import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import crmService from '../../services/crmService';
import jobApplicationService from '../../services/jobApplicationService';
import HeaderNavigation from '../../components/ui/HeaderNavigation';
import MainSidebar from '../../components/ui/MainSidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import LeadPipeline from './components/LeadPipeline';
import LeadTable from './components/LeadTable';
import LeadDetailModal from './components/LeadDetailModal';
import AddLeadModal from './components/AddLeadModal';
import AssignLeadModal from './components/AssignLeadModal';
import AssignHRModal from './components/AssignHRModal';
import NotificationToast from '../../components/ui/NotificationToast';

const CRMLeadManagement = () => {
  const { user, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const { isCollapsed } = useSidebar();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pipelineStats, setPipelineStats] = useState({});
  const [telecallers, setTelecallers] = useState([]);
  const [hrs, setHRs] = useState([]);

  // Modal states
  const [showLeadDetailModal, setShowLeadDetailModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAssignHRModal, setShowAssignHRModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);

  // Handle URL actions
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'add') {
      setShowAddLeadModal(true);
    }
  }, [searchParams]);

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    source: '',
    skill_level: '',
    telecaller_id: ''
  });

  // Notification state
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [leadsResult, statsResult, telecallersResult, hrsResult] = await Promise.all([
          crmService.getLeads(),
          crmService.getLeadPipelineStats(),
          crmService.getTelecallers(),
          crmService.getHRs()
        ]);

        if (leadsResult?.success) {
          setLeads(leadsResult.data || []);
          setFilteredLeads(leadsResult.data || []);
        } else {
          setError(leadsResult?.error || 'Failed to load leads');
        }

        if (statsResult?.success) {
          setPipelineStats(statsResult.data || {});
        }

        if (telecallersResult?.success) {
          setTelecallers(telecallersResult.data || []);
        }

        if (hrsResult?.success) {
          setHRs(hrsResult.data || []);
        }

      } catch (error) {
        setError('Failed to load CRM data');
        console.log('CRM data loading error:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...leads];

    const parseSkills = (skills) => {
      if (!skills) return [];
      if (Array.isArray(skills)) return skills;
      if (typeof skills === 'string') {
        try {
          const parsed = JSON.parse(skills);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return skills.split(',').map(s => s.trim()).filter(Boolean);
        }
      }
      return [];
    };

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(lead => {
        const nameMatch = lead?.name?.toLowerCase().includes(searchLower);
        const emailMatch = lead?.email?.toLowerCase().includes(searchLower);
        const phoneMatch = lead?.phone?.includes(searchLower);
        const parsedSkills = parseSkills(lead?.skills);
        const skillsMatch = parsedSkills.some(skill => skill.toLowerCase().includes(searchLower));
        return nameMatch || emailMatch || phoneMatch || skillsMatch;
      });
    }

    if (filters.status) {
      filtered = filtered.filter(lead => lead?.status === filters.status);
    }

    if (filters.source) {
      filtered = filtered.filter(lead => lead?.source === filters.source);
    }

    if (filters.skill_level) {
      filtered = filtered.filter(lead => lead?.skill_level === filters.skill_level);
    }

    if (filters.telecaller_id) {
      filtered = filtered.filter(lead => lead?.assigned_telecaller_id === filters.telecaller_id);
    }

    setFilteredLeads(filtered);
  }, [leads, filters]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      source: '',
      skill_level: '',
      telecaller_id: ''
    });
  };

  const handleLeadSelect = (leadId, selected) => {
    setSelectedLeads(prev =>
      selected
        ? [...prev, leadId]
        : prev.filter(id => id !== leadId)
    );
  };

  const handleSelectAll = (selected) => {
    setSelectedLeads(selected ? filteredLeads.map(lead => lead.id) : []);
  };

  const handleViewLead = (lead) => {
    setSelectedLead(lead);
    setShowLeadDetailModal(true);
  };

  const handleAssignLeads = () => {
    if (selectedLeads.length === 0) {
      showNotification('Please select leads to assign', 'error');
      return;
    }
    setShowAssignModal(true);
  };

  const handleAssignHR = () => {
    if (selectedLeads.length === 0) {
      showNotification('Please select leads to assign', 'error');
      return;
    }
    setShowAssignHRModal(true);
  };

  const handleLeadAdded = async (newLead) => {
    setLeads(prev => [newLead, ...prev]);
    showNotification('Lead added successfully');
    setShowAddLeadModal(false);

    // Refresh stats
    const statsResult = await crmService.getLeadPipelineStats();
    if (statsResult?.success) {
      setPipelineStats(statsResult.data || {});
    }
  };

  const handleLeadUpdated = async (updatedLead) => {
    setLeads(prev => prev.map(lead =>
      lead.id === updatedLead.id ? updatedLead : lead
    ));
    showNotification('Lead updated successfully');

    // Refresh stats
    const statsResult = await crmService.getLeadPipelineStats();
    if (statsResult?.success) {
      setPipelineStats(statsResult.data || {});
    }
  };

  const handleLeadsAssigned = async (assignedLeads) => {
    setLeads(prev => prev.map(lead => {
      const assignedLead = assignedLeads.find(al => al.id === lead.id);
      return assignedLead || lead;
    }));
    setSelectedLeads([]);
    setShowAssignModal(false);
    setShowAssignHRModal(false);
    showNotification(`${assignedLeads.length} leads assigned successfully`);
  };

  const handleDeleteLead = async (leadId) => {
    try {
      const result = await crmService.deleteLead(leadId);
      if (result?.success) {
        setLeads(prev => prev.filter(lead => lead.id !== leadId));
        showNotification('Lead deleted successfully');

        // Refresh stats
        const statsResult = await crmService.getLeadPipelineStats();
        if (statsResult?.success) {
          setPipelineStats(statsResult.data || {});
        }
      } else {
        showNotification(result?.error || 'Failed to delete lead', 'error');
      }
    } catch (error) {
      showNotification('An error occurred while deleting the lead', 'error');
      console.log('Delete lead error:', error);
    }
  };

  const handleMoveToATS = async (lead) => {
    if (!lead?.id) return;
    if (!window.confirm(`Move lead "${lead.name}" to Applicant Tracking System?`)) return;

    try {
      const result = await jobApplicationService.importFromLeads({ lead_ids: [lead.id] });

      if (result?.success && result.data?.successful > 0) {
        showNotification('Lead moved to ATS successfully');
        // Update local lead state
        const updatedLead = {
          ...lead,
          status: 'moved_to_ats',
          notes: (lead?.notes || '') + `\n[Imported to Applicant Tracking System on ${new Date().toLocaleString()}]`
        };
        handleLeadUpdated(updatedLead);
      } else {
        const errorMessage = result.data?.errors?.[0] || result?.error || 'Failed to move lead to ATS (possibly duplicate email)';
        showNotification(errorMessage, 'error');
      }
    } catch (error) {
      showNotification('An error occurred while moving lead to ATS', 'error');
      console.error('Move to ATS error:', error);
    }
  };

  const activeFiltersCount = Object.values(filters).filter(value => value !== '').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="text-muted-foreground">Loading CRM data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <HeaderNavigation
        onSidebarToggle={() => setSidebarOpen(true)}
        user={user}
        userProfile={userProfile}
      />

      <MainSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className={`min-h-screen pt-16 transition-all duration-300 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                  CRM Lead Management
                </h1>
                <p className="text-muted-foreground mt-1">
                  Manage applicant leads from initial contact through client placement
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  onClick={() => setShowAddLeadModal(true)}
                  className="flex items-center gap-2"
                >
                  <Icon name="Plus" size={16} />
                  Add Lead
                </Button>

                {selectedLeads.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={handleAssignLeads}
                      className="flex items-center gap-2"
                    >
                      <Icon name="Phone" size={16} />
                      Assign Call ({selectedLeads.length})
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleAssignHR}
                      className="flex items-center gap-2"
                    >
                      <Icon name="Briefcase" size={16} />
                      Assign HR ({selectedLeads.length})
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
              <div className="flex items-center gap-2 text-destructive">
                <Icon name="AlertCircle" size={16} />
                <p className="font-medium">Error loading leads</p>
              </div>
              <p className="text-sm text-destructive/80 mt-1">{error}</p>
            </div>
          )}

          {/* Lead Pipeline */}
          <div className="mb-6 sm:mb-8">
            <LeadPipeline stats={pipelineStats} />
          </div>

          {/* Filters */}
          <div className="mb-6 p-4 sm:p-6 bg-card border border-border rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Filters</h3>
              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-muted-foreground"
                >
                  Clear ({activeFiltersCount})
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <Input
                placeholder="Search leads..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="col-span-1 sm:col-span-2"
              />

              <Select
                value={filters.status}
                onValueChange={(value) => handleFilterChange('status', value)}
                placeholder="Status"
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'new', label: 'New' },
                  { value: 'contacted', label: 'Contacted' },
                  { value: 'validated', label: 'Validated' },
                  { value: 'shortlisted', label: 'Shortlisted' },
                  { value: 'placed', label: 'Placed' },
                  { value: 'rejected', label: 'Rejected' },
                  { value: 'moved_to_ats', label: 'Moved to ATS' }
                ]}
              />

              <Select
                value={filters.source}
                onValueChange={(value) => handleFilterChange('source', value)}
                placeholder="Source"
                options={[
                  { value: '', label: 'All Sources' },
                  { value: 'direct_contact', label: 'Direct Contact' },
                  { value: 'job_portal', label: 'Job Portal' },
                  { value: 'referral', label: 'Referral' },
                  { value: 'website', label: 'Website' },
                  { value: 'social_media', label: 'Social Media' }
                ]}
              />

              <Select
                value={filters.skill_level}
                onValueChange={(value) => handleFilterChange('skill_level', value)}
                placeholder="Skill Level"
                options={[
                  { value: '', label: 'All Levels' },
                  { value: 'entry', label: 'Entry' },
                  { value: 'intermediate', label: 'Intermediate' },
                  { value: 'senior', label: 'Senior' },
                  { value: 'expert', label: 'Expert' }
                ]}
              />

              <Select
                value={filters.telecaller_id}
                onValueChange={(value) => handleFilterChange('telecaller_id', value)}
                placeholder="Telecaller"
                options={[
                  { value: '', label: 'All Telecallers' },
                  ...(telecallers?.map(telecaller => ({
                    value: telecaller.id,
                    label: telecaller.full_name
                  })) || [])
                ]}
              />
            </div>
          </div>

          {/* Leads Table */}
          <div className="bg-card border border-border rounded-lg">
            <LeadTable
              leads={filteredLeads}
              selectedLeads={selectedLeads}
              onLeadSelect={handleLeadSelect}
              onSelectAll={handleSelectAll}
              onViewLead={handleViewLead}
              onEditLead={handleViewLead}
              onDeleteLead={handleDeleteLead}
              onMoveToATS={handleMoveToATS}
              telecallers={telecallers}
            />
          </div>
        </div>
      </main>

      {/* Modals */}
      {showLeadDetailModal && selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setShowLeadDetailModal(false)}
          onLeadUpdated={handleLeadUpdated}
        />
      )}

      {showAddLeadModal && (
        <AddLeadModal
          onClose={() => setShowAddLeadModal(false)}
          onLeadAdded={handleLeadAdded}
        />
      )}

      {showAssignModal && (
        <AssignLeadModal
          leadIds={selectedLeads}
          telecallers={telecallers}
          onClose={() => setShowAssignModal(false)}
          onLeadsAssigned={handleLeadsAssigned}
        />
      )}

      {showAssignHRModal && (
        <AssignHRModal
          leadIds={selectedLeads}
          hrs={hrs}
          onClose={() => setShowAssignHRModal(false)}
          onLeadsAssigned={handleLeadsAssigned}
        />
      )}

      {/* Notification */}
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
};

export default CRMLeadManagement;